import { useLingui } from "@lingui/react/macro";
import useSWRSubscription from "swr/subscription";

export interface AppConfigMap {
  [key: string]: string | undefined;
  feature_poke_audio_enabled?: string;
  feature_poke_drawing_enabled?: string;
  feature_poke_emoji_enabled?: string;
  feature_poke_geolocation_enabled?: string;
  feature_poke_normal_enabled?: string;
  feature_poke_picture_enabled?: string;
  intro_app_description?: string;
  intro_version?: string;
  intro_version_description?: string;
  search_user_strategy?: string;
}

interface ConfigStreamEvent {
  payload: ConfigStreamPayload;
  type: "CONFIG_UPDATE";
}

interface ConfigStreamPayload {
  configs?: AppConfigMap;
  key?: string;
  value?: string;
}

export const useAppConfig = () => {
  const { i18n, t } = useLingui();
  const locale = i18n.locale;

  const { data: configData, error } = useSWRSubscription<
    AppConfigMap,
    Error,
    [string]
  >(["util/app-config"], (_key, { next }) => {
    const apiUrl = import.meta.env.VITE_YOGI_POKE_API_URL;
    const streamUrl = `${apiUrl}/util/app-config`;
    const es = new EventSource(streamUrl);

    es.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data as string) as ConfigStreamEvent;
        if (parsed.payload.configs) {
          next(null, parsed.payload.configs);
        } else if (parsed.payload.key && parsed.payload.value !== undefined) {
          const updatedKey = parsed.payload.key;
          const updatedVal = parsed.payload.value;
          next(null, (prev) => ({
            ...(prev ?? {}),
            [updatedKey]: updatedVal,
          }));
        }
      } catch (err) {
        console.error("Failed to parse config stream event:", err);
      }
    };

    es.onerror = () => {
      const streamErr = new Error("Config stream EventSource error");
      console.error(streamErr.message);
      next(streamErr);
    };

    return () => {
      es.close();
    };
  });

  const isLoading = !configData && !error;

  const isNormalPokeEnabled =
    configData?.feature_poke_normal_enabled !== "false";
  const isAudioPokeEnabled = configData?.feature_poke_audio_enabled !== "false";
  const isPicturePokeEnabled =
    configData?.feature_poke_picture_enabled !== "false";
  const isDrawingPokeEnabled =
    configData?.feature_poke_drawing_enabled !== "false";
  const isGeolocationPokeEnabled =
    configData?.feature_poke_geolocation_enabled !== "false";
  const isEmojiPokeEnabled = configData?.feature_poke_emoji_enabled !== "false";

  const isPokeEnabled =
    isNormalPokeEnabled ||
    isAudioPokeEnabled ||
    isPicturePokeEnabled ||
    isDrawingPokeEnabled ||
    isGeolocationPokeEnabled ||
    isEmojiPokeEnabled;

  const introAppDescription =
    configData?.[`intro_app_description_${locale}`] ??
    configData?.intro_app_description ??
    t`빠르고 간결한 최신 소셜 미디어`;
  const introVersion =
    configData?.[`intro_version_${locale}`] ??
    configData?.intro_version ??
    "25.01.28";
  const introVersionDescription =
    configData?.[`intro_version_description_${locale}`] ??
    configData?.intro_version_description ??
    t`가까운 친구를 팔로우하세요`;

  return {
    config: configData ?? {},
    error: error as unknown,
    introAppDescription,
    introVersion,
    introVersionDescription,
    isAudioPokeEnabled,
    isDrawingPokeEnabled,
    isEmojiPokeEnabled,
    isGeolocationPokeEnabled,
    isLoading,
    isNormalPokeEnabled,
    isPicturePokeEnabled,
    isPokeEnabled,
    searchUserStrategy: configData?.search_user_strategy ?? "latest",
  };
};
